<template>
	<div class="my-4 px-4 group-body">
		<div>
			<SyncedGroupConnection class="pb-2" :group="group" @selected-app="appSelected" @selected-connection-id="connectionIdSelected" />
			<ChooseWhatAndHowToSync
				v-if="selectedApp"
				:group="group"
				:service="selectedApp"
				@selected-sync-what-type="whatAndHowToSyncTypeSelected"
				@selected-on-delete-type="onTeamDeletionTypeSelected"
				@selected-specific-groups="specificGroupsSelected"
			/>
			<ChooseSyncAction v-if="selectedApp" :group="group" :service="selectedApp" @selected-action-type="actionTypeSelected" />
			<Row class="items-center justify-between mb-6">
				<div class="section-title">Workspace role for invited members</div>
				<RoleSelector :recommendedRole="recommendedRole" :value="localUserRole" :onChange="(role) => (localUserRole = role)" />
			</Row>
		</div>
		<AnemicButton
			tile
			:loading="isSaving"
			@click="saveGroupChanges"
			:disabled="isSaveDisabled"
			v-if="didClickContinueOnce"
			class="px-2 continue-and-invite-button"
		>
			{{ verifyMailInvitationText }}
		</AnemicButton>
		<AnemicButton v-else :loading="isSaving" @click="saveGroupChanges" :disabled="isSaveDisabled" class="save-button">
			Save changes
		</AnemicButton>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import ChooseSyncAction from './SyncedGroupOptions/ChooseSyncAction';
import ChooseWhatAndHowToSync from './SyncedGroupOptions/ChooseWhatAndHowToSync';
import SyncedGroupConnection from './SyncedGroupOptions/SyncedGroupConnection';
import { BILLING_LEVEL_TO_RECOMMENDED_ROLE } from '@/components/Billing/billing-service.const';
import RoleSelector from '@/components/ReactComponentsDataProviders/RoleSelector.vue';
import { createGroup, updateGroup } from '@/store/modules/accountGroups/actions';
import { groups, defaultGroupName } from '@/store/modules/accountGroups/getters';
import { AccountRoles } from '@/utils/account-service.const';
import { SYNC_USERS_ACTION_TYPE } from '@/utils/groups-const';
export default {
	name: 'SyncedGroupOptionsTab',
	components: {
		SyncedGroupConnection,
		ChooseWhatAndHowToSync,
		ChooseSyncAction,
		RoleSelector,
	},
	props: {
		group: { type: Object },
	},
	computed: {
		...mapGetters([groups.fullPath, defaultGroupName.fullPath]),
		isSaveDisabled() {
			return (
				!this.didSelectedAppChange &&
				!this.didConnectionIdChange &&
				!this.didSyncUsersActionChanged &&
				!this.didSyncWhatAndHowToSyncTypeChanged &&
				!this.didOnDeletionTypeChanged &&
				!this.didSpecificGroupChanged &&
				!this.didInvitedUserRoleChange
			);
		},
		didInvitedUserRoleChange() {
			return this.localUserRole !== this.group.synced.invite_user_role;
		},
		didSelectedAppChange() {
			return this.selectedApp?.id !== this.group.synced.service;
		},
		didConnectionIdChange() {
			return this.selectedConnectionId !== this.group.synced.credentials;
		},
		didSyncUsersActionChanged() {
			return this.selectedActionType !== this.group.synced.sync_users_action_type;
		},
		didSyncWhatAndHowToSyncTypeChanged() {
			return this.whatAndHowToSyncType !== this.group.synced.what_to_sync_type;
		},
		didOnDeletionTypeChanged() {
			return this.onDeletionType !== this.group.synced.on_delete_group_action_type;
		},
		didSpecificGroupChanged() {
			return (
				this.syncSpecificGroups.length !== this.group.synced.subgroups_to_sync.length ||
				this.syncSpecificGroups.some((x) => !this.group.synced.subgroups_to_sync.includes(x)) ||
				this.group.synced.subgroups_to_sync.some((x) => !this.syncSpecificGroups.includes(x))
			);
		},
		verifyMailInvitationText() {
			return `Send invitation emails to new synced users`;
		},
	},
	methods: {
		...mapActions([createGroup.fullPath, updateGroup.fullPath]),
		async saveGroupChanges() {
			if (!this.didClickContinueOnce && this.didSyncUsersActionChanged && this.selectedActionType === SYNC_USERS_ACTION_TYPE.INVITE_NEW) {
				this.didClickContinueOnce = true;
				return;
			}
			this.isSaving = true;
			try {
				await this[updateGroup.fullPath]({
					id: this.group.id,
					name: this.group.name,
					synced: {
						service: this.selectedApp.id,
						credentials: this.selectedConnectionId,
						sync_users_action_type: this.selectedActionType,
						what_to_sync_type: this.whatAndHowToSyncType,
						on_delete_group_action_type: this.onDeletionType,
						subgroups_to_sync: this.syncSpecificGroups,
						invite_user_role: this.localUserRole,
						is_active: true,
					},
				});
				this.$toast.success('Group updated');
			} catch {
				this.$toast.error(`Failed to update group, please try again`);
			} finally {
				this.isSaving = false;
				this.didClickContinueOnce = false;
			}
		},
		appSelected(app) {
			this.selectedApp = app;
		},
		connectionIdSelected(connectionId) {
			this.selectedConnectionId = connectionId;
		},
		actionTypeSelected(actionType) {
			this.didClickContinueOnce = false;
			this.selectedActionType = actionType;
		},
		whatAndHowToSyncTypeSelected(howType) {
			this.whatAndHowToSyncType = howType;
		},
		onTeamDeletionTypeSelected(onDeleteType) {
			this.onDeletionType = onDeleteType;
		},
		specificGroupsSelected(syncSpecificGroups) {
			this.syncSpecificGroups = syncSpecificGroups;
		},
	},
	created() {
		this.selectedConnectionId = this.group.synced.credentials;
		this.selectedActionType = this.group.synced.sync_users_action_type;
		this.whatAndHowToSyncType = this.group.synced.what_to_sync_type;
		this.onDeletionType = this.group.synced.on_delete_group_action_type;
		this.syncSpecificGroups = this.group.synced.subgroups_to_sync;
		this.localUserRole = this.group.synced.invite_user_role || this.recommendedRole || AccountRoles.LiteEndUser;
	},

	data: () => ({
		searchString: '',
		notFound: false,
		selectedConnectionId: undefined,
		selectedApp: undefined,
		selectedActionType: undefined,
		whatAndHowToSyncType: undefined,
		onDeletionType: undefined,
		syncSpecificGroups: undefined,
		isSaving: false,
		didClickContinueOnce: false,
		localUserRole: undefined,
		recommendedRole: undefined,
	}),
	watch: {
		selectedApp: {
			handler(newV) {
				this.recommendedRole = BILLING_LEVEL_TO_RECOMMENDED_ROLE[newV?.billing_level];
			},
			immediate: true,
		},
	},
};
</script>

<style scoped lang="scss">
.group-body {
	height: 380px !important;
	overflow: auto !important;
}
.save-button {
	width: 144px !important;
	height: 38px !important;
	font-size: 14px !important;
	border-radius: 3px !important;
	letter-spacing: 0px !important;
	color: var(--white) !important;
	font-weight: normal !important;
	border-color: transparent !important;
	background: var(--forth-color) !important;
}
.continue-and-invite-button {
	color: #5f60dc !important;
	border-radius: 5px !important;
	text-transform: none !important;
	background: #ffffff !important;
	border: 1px solid #5f60dc !important;
}
</style>
